import { useState } from 'react';
import { Loader2, Split, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useFinance } from '@/contexts/FinanceContext';
import { DREFilters } from '@/features/dre/DREFilters';
import { formatBRL, formatPeriodLabel } from '@/features/dre/dreUtils';
import { ChannelResultTable } from './ChannelResultTable';
import { useChannelResult } from './useChannelResult';
import type { ChannelPeriodFilter } from './types';

export function ChannelResultPage() {
  const { client, clientId } = useCurrentClient();
  const { isLoading } = useFinance();
  const today = new Date();
  const [filter, setFilter] = useState<ChannelPeriodFilter>({
    year: today.getFullYear(),
    fromMonth: today.getMonth() + 1,
    toMonth: today.getMonth() + 1,
  });

  const { report, from, to } = useChannelResult(filter);

  if (!clientId) {
    return (
      <div>
        <h1 className="mb-1 font-brand text-2xl font-bold text-foreground">Resultado por Canal</h1>
        <Card>
          <CardContent className="p-12 text-center text-muted-foreground">
            <Split className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="text-sm">Selecione um cliente no topo para ver o resultado por canal.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Resultado por Canal</h1>
          <p className="text-sm text-muted-foreground">
            {formatPeriodLabel(from, to)} · {client?.name ?? ''}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <DREFilters filter={filter} onChange={setFilter} />
        </CardContent>
      </Card>

      {/* Resumo em 3 cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Receita total
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-income">
              {formatBRL(report.totalRevenue)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Margem de contribuição
            </p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">
              {formatBRL(report.totalContributionMargin)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Lucro líquido
            </p>
            <p
              className={`mt-1 text-2xl font-bold tabular-nums ${report.totalNetProfit >= 0 ? 'text-income' : 'text-expense'}`}
            >
              {formatBRL(report.totalNetProfit)}
            </p>
          </CardContent>
        </Card>
      </div>

      {report.hasUnassignedRevenue && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>
            Há receita lançada <strong>sem canal</strong> (sem centro de custo). Ela aparece na linha
            "Sem canal". Atribua um centro de custo a esses lançamentos para que o resultado por canal
            fique completo. Você pode fazer isso em massa nas Transações.
          </p>
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : (
        <ChannelResultTable report={report} />
      )}

      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          <strong>Canal</strong> = centro de custo. Considera apenas lançamentos com status{' '}
          <strong>Pago</strong>.
        </p>
        <p>
          <strong>Margem de contribuição</strong> = receita do canal menos os custos diretos
          carimbados nele (entrega, comissão, taxas).
        </p>
        <p>
          <strong>Lucro líquido</strong> = margem menos o rateio dos custos fixos. Custo fixo =
          despesa sem centro de custo, rateada entre os canais por % da receita.
        </p>
      </div>
    </div>
  );
}
